// ─────────────────────────────────────────────────────────────────────────────
// C2 — «Brecha Generacional × Fundamental», hoja del Excel `04 - Demografico`.
//
// «Puntuación promedio de reconocimiento (1–4) por generación y Fundamental. Δ Brecha ≥ 1.0 = crítica.»
//
// De dónde sale el 1–4. En un primer análisis lo di por NO construible: busqué una puntuación en
// `Recognition` y no existe —un reconocimiento se CUENTA, no se califica—. Pero los encabezados de la
// hoja son los cuatro Fundamentales, y esa puntuación sí existe: es la de Pulso
// (`ParticipantScore.FundamentalAggregates`, con `ScoreAggregate.Average` en escala 1–4), y vive en la
// MISMA base de AnalyticsService, ya indexada por `GenerationBand`. No hacía falta métrica nueva ni
// backend nuevo: `GET /v1/analytics/generational` ya devuelve exactamente generación × Fundamental ×
// promedio.
//
// La Δ Brecha tampoco es un cálculo del servidor: es MÁXIMO − MÍNIMO de la fila. Comprobado contra los
// números de la propia hoja — Boomer 3.6−2.8 = 0.8, Gen Z 3.9−2.7 = 1.2.
//
// Por qué la tabla se arma aquí y no en el backend: es una PIVOTACIÓN de datos que ya viajan. Pedirle
// al servidor una consulta nueva para transponer una lista sería duplicar la fuente de verdad de la
// puntuación en dos sitios, que es como acaban discrepando.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsGenerational } from '@/hooks/useAnalytics';
import type { AnalyticsFilterParams } from '@/api/client';
import { useRecognitionCatalog, getFundamentalColor } from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import { fundamentalTintStyle, INTENSIDAD_MINIMA_SIN_DATO, filtrosActivosDe } from '../reportsShared';
import { claseDeBanda, etiquetaDeBanda } from './DistribucionGeneracional';
import { Paginacion, usePaginacion } from '../Paginacion';

/** Δ a partir de la cual la hoja marca la brecha como crítica. */
export const UMBRAL_BRECHA_CRITICA = 1.0;

/**
 * Máximo valor de cada columna (Fundamental) entre TODAS las filas, no solo la página visible —
 * la escala de color debe ser estable sin importar en qué página está cada generación. Mismo
 * criterio que `maximoDeColumna` del Mapa de Calor OU × Fundamental: nunca 0, para no dividir
 * entre cero con una columna sin ningún valor medido.
 */
export function maximosPorColumna(
  fundamentales: readonly (readonly [string, string])[], filas: readonly Fila[],
): Map<string, number> {
  const porId = new Map<string, number>();
  for (const [id] of fundamentales) {
    const valores = filas.map((fila) => fila.porFundamental.get(id)).filter((v): v is number => v !== undefined);
    porId.set(id, Math.max(1, ...valores));
  }
  return porId;
}

interface Fila {
  generacion: string;
  porFundamental: Map<string, number>;
  brecha: number | null;
}

/**
 * Pivota la respuesta a la matriz de la hoja y calcula la Δ de cada fila.
 *
 * Exportada para poder probarla sin montar la pantalla: es la única parte con reglas —qué cuenta como
 * medido, cómo se calcula la brecha— y probarla a través del render la dejaría a merced del contexto.
 */
export function construirMatriz(grupos: ReturnType<typeof useAnalyticsGenerational>['data']) {
  // Orden canónico: el de CUALQUIER grupo, porque el backend envía la MISMA lista completa y
  // ordenada por catálogo (`fundamental.Order`) en cada generación — no depende de qué generación
  // tiene dato. Un Map acumulado grupo a grupo se desordena en cuanto una generación se salta un
  // Fundamental sin medir; leer el orden de un solo grupo, siempre, lo evita.
  const ordenCanonico = new Map<string, string>();
  for (const dimension of grupos?.[0]?.dimensions ?? []) {
    ordenCanonico.set(dimension.fundamentalId, dimension.fundamentalName);
  }

  const filas: Fila[] = [];

  for (const grupo of grupos ?? []) {
    const sumaPorFundamental = new Map<string, number>();
    const conteoPorFundamental = new Map<string, number>();

    for (const d of grupo.dimensions ?? []) {
      // Una dimensión sin medir llega con `average = 0`, que NO es una puntuación baja: la escala
      // empieza en 1. Tratarla como 0 hundiría el mínimo y convertiría cualquier fila en «crítica».
      if (!d.average || d.average <= 0) continue;
      if (!ordenCanonico.has(d.fundamentalId)) ordenCanonico.set(d.fundamentalId, d.fundamentalName);

      // Varias dimensiones pueden colgar del mismo Fundamental (comportamientos): se promedia por
      // Fundamental (media aritmética real, no un acumulado ponderado hacia el último valor), que
      // es el eje que la hoja pide.
      sumaPorFundamental.set(d.fundamentalId, (sumaPorFundamental.get(d.fundamentalId) ?? 0) + d.average);
      conteoPorFundamental.set(d.fundamentalId, (conteoPorFundamental.get(d.fundamentalId) ?? 0) + 1);
    }

    const porFundamental = new Map<string, number>();
    for (const [fundamentalId, suma] of sumaPorFundamental) {
      porFundamental.set(fundamentalId, suma / (conteoPorFundamental.get(fundamentalId) ?? 1));
    }

    const valores = [...porFundamental.values()];
    filas.push({
      generacion: grupo.generationBand,
      porFundamental,
      // Con menos de DOS Fundamentales medidos no hay brecha que calcular: una diferencia necesita
      // dos extremos. Se devuelve null y la celda dice «—», en vez de un 0 que se leería como
      // «generación perfectamente pareja».
      brecha: valores.length < 2
        ? null
        : Math.round((Math.max(...valores) - Math.min(...valores)) * 10) / 10,
    });
  }

  return { fundamentales: [...ordenCanonico.entries()], filas };
}

export function BrechaGeneracionalTable(
  { filters, params }: Readonly<{
    filters?: AnalyticsFilterParams;
    /**
     * Fundamental/persona activos en la tira. La brecha sale de PROMEDIOS DE PULSO por generación y
     * Fundamental (`useAnalyticsGenerational`), no de la actividad de reconocimiento ni del censo de
     * plantilla: ninguno de los dos filtros la acota, a diferencia de otras secciones del panel.
     */
    params?: Pick<RecognitionReportParams, 'fundamentalId' | 'personaId'>;
  }>,
) {
  const { t } = useTranslation('recognition');
  const q = useAnalyticsGenerational(filters ?? {});
  const { data: catalog } = useRecognitionCatalog();

  const { fundamentales, filas } = useMemo(() => construirMatriz(q.data), [q.data]);
  const filtrosActivos = useMemo(() => filtrosActivosDe(params ?? {}), [params]);

  // Color de cada columna: el mismo del catálogo de Fundamentales que usa el resto de la
  // Reportería (`getFundamentalColor`), no una paleta inventada para esta tabla.
  const colorPorFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    (catalog ?? []).forEach((f, indice) => porId.set(f.id, getFundamentalColor(f, indice)));
    return porId;
  }, [catalog]);

  const maximosDeColumna = useMemo(() => maximosPorColumna(fundamentales, filas), [fundamentales, filas]);

  const { visibles, pagina, totalPaginas, total, tamano, setPagina, cambiarTamano } = usePaginacion(filas);

  // Sin datos no se pinta nada: una matriz vacía con encabezados haría creer que las generaciones
  // puntúan cero, y cero no está en la escala.
  if (filas.length === 0 || fundamentales.length === 0) return null;

  return (
    <div className="rr-table-wrap rr-table-wrap--top-gap">
      <div className="au-card-sub rr-block-title--tight">
        {t('reports.generational.gapTitle')}
        <i
          className="fas fa-circle-info rr-info-icon"
          aria-hidden="true"
          title={t('reports.generational.gapTitleHelp')}
        />
      </div>
      <div className="rr-muted rr-muted--gap">
        {t('reports.generational.gapSubtitle')}
      </div>
      {/* Punto 9 (revisión final) — `promedioPulso`, no `censo`: esta tabla sale de
          `useAnalyticsGenerational` (promedios de Pulso por generación y Fundamental), no del censo
          de plantilla. El aviso estaba bien puesto; la explicación era de otra sección. */}
      <AvisoFiltroNoAplica filtros={filtrosActivos} motivo="promedioPulso" />
      <div className="au-card rr-brecha-card">
        <table className="rr-table">
          <thead>
            <tr>
              <th>{t('reports.generational.colGeneration')}</th>
              {fundamentales.map(([id, nombre]) => (
                <th key={id} className="rr-num" style={{ color: colorPorFundamental.get(id) }}>
                  {nombre}
                </th>
              ))}
              <th className="rr-num">{t('reports.generational.colGap')}</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((fila) => {
              const esCritica = fila.brecha !== null && fila.brecha >= UMBRAL_BRECHA_CRITICA;
              return (
                <tr key={fila.generacion}>
                  <td>
                    <span className={`rr-gen-dot-mini ${claseDeBanda(fila.generacion)}`} />
                    {etiquetaDeBanda(fila.generacion)}
                  </td>
                  {fundamentales.map(([id]) => {
                    const v = fila.porFundamental.get(id);
                    const color = colorPorFundamental.get(id) ?? '#6b7280';
                    // «—», no 0: una casilla sin medir no es una puntuación mínima. Mismo esquema de
                    // color que el Mapa de Calor OU × Fundamental: tinte de la columna, casi
                    // invisible sin dato, con intensidad relativa al máximo de la columna si lo hay.
                    return (
                      <td
                        key={id}
                        className="rr-num"
                        style={v === undefined
                          ? fundamentalTintStyle(color, 0, INTENSIDAD_MINIMA_SIN_DATO)
                          : fundamentalTintStyle(color, v / (maximosDeColumna.get(id) ?? 1))}
                      >
                        {v === undefined ? '—' : v.toFixed(1)}
                      </td>
                    );
                  })}
                  <td className={`rr-num--strong ${esCritica ? 'rr-gap-critical' : ''}`}>
                    {esCritica && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
                    {fila.brecha === null ? '—' : fila.brecha.toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          total={total}
          tamano={tamano}
          onPagina={setPagina}
          onTamano={cambiarTamano}
          siempreVisible
        />
      </div>
    </div>
  );
}
